import React from 'react';
import { MapPin, Leaf, TreePine, Sprout, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../utils/mockData';
import BrazilMap from './BrazilMap';

// Territories Section Component
export const TerritoriesSection = ({ activeTerritory, setActiveTerritory }) => {
  return (
    <section id="territorios" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl md:text-5xl font-bold text-ecam-forest mb-6">
            O Carbono Social está transformando a vida de centenas de famílias por todo o Brasil
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Cada região tem uma história, uma cultura, um povo e um impacto único.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {mockData.territories.map((territory, index) => (
            <Card 
              key={index} 
              className="overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2 animate-on-scroll cursor-pointer"
              style={{ animationDelay: `${index * 150}ms` }}
              onMouseEnter={() => setActiveTerritory(territory.name)}
              onMouseLeave={() => setActiveTerritory(null)}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={territory.image} 
                  alt={territory.name}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ecam-forest/90 via-ecam-forest/40 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-5 w-5" />
                    <span className="text-sm font-semibold">{territory.region}</span>
                  </div>
                  <h3 className="text-2xl font-bold">{territory.name}</h3>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-sm text-ecam-blue font-semibold mb-1">{territory.partner}</p>
                {territory.project && (
                  <p className="text-xs text-gray-600 italic mb-3">{territory.project}</p>
                )}
                <p className="text-gray-700 leading-relaxed mb-4">{territory.description}</p>
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2 text-ecam-green font-semibold">
                    <Leaf className="h-4 w-4" />
                    <span>{territory.families}</span>
                  </div>
                  <div className="flex items-center gap-2 text-ecam-blue font-semibold">
                    <TreePine className="h-4 w-4" />
                    <span>{territory.carbon}</span>
                  </div>
                </div>
                {territory.carbonNote && (
                  <p className="text-xs text-gray-500 italic mt-2">{territory.carbonNote}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Impact Numbers Section
export const ImpactNumbersSection = () => {
  // Separar o item de CO2 dos outros
  const co2Item = mockData.impactNumbers.find(item => item.label.includes('CO₂'));
  const otherItems = mockData.impactNumbers.filter(item => !item.label.includes('CO₂'));
  
  return (
    <section id="impacto" className="py-24 bg-ecam-petrol text-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="images/svg/Folhas_Espiral_Ecam.svg"
          alt="" 
          className="absolute right-0 top-0 h-full"
        />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Impacto em Números</h2>
          <p className="text-xl opacity-90">Resultados reais, pessoas reais, impacto real</p>
        </div>

        {/* Destaque CO2 - Grande no topo */}
        <div className="text-center mb-20 animate-on-scroll">
          <div className="inline-block relative">
            {/* Animação de pulso suave */}
            <div className="absolute inset-0 bg-ecam-yellow/20 rounded-full blur-2xl animate-pulse"></div>
            
            <div className="relative bg-ecam-forest/30 backdrop-blur-sm rounded-3xl px-8 py-10 md:px-12 md:py-12 border-4 border-ecam-yellow/30 shadow-2xl transform hover:scale-105 transition-transform duration-500">
              {/* Ícone */}
              <div className="mb-4 flex justify-center animate-bounce-slow">
                {co2Item?.icon}
              </div>
              
              {/* Número grande com animação */}
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-ecam-yellow mb-3 tracking-tight animate-pulse-slow">
                3.000.000 <span className="text-2xl md:text-3xl lg:text-4xl">tCO₂</span>
              </div>
              
              {/* Texto descritivo */}
              <p className="text-lg md:text-xl font-medium opacity-90 mt-3">
                Toneladas de CO₂ de emissões evitadas*
              </p>
            </div>
          </div>
        </div>

        {/* Outros números - Grid abaixo */}
        <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {otherItems.map((item, index) => (
            <div 
              key={index} 
              className="text-center animate-on-scroll bg-ecam-forest/20 rounded-xl p-6 backdrop-blur-sm border border-ecam-yellow/10 hover:border-ecam-yellow/30 transition-all duration-300 hover:transform hover:scale-105"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="mb-4 flex justify-center">
                {item.icon}
              </div>
              <div className="text-3xl md:text-4xl font-bold text-ecam-yellow mb-3 counter-number">
                {item.number}
              </div>
              <p className="text-base md:text-lg opacity-90">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Map Section - Usando componente BrazilMap interativo
export const MapSection = ({ activeTerritory }) => {
  return <BrazilMap />;
};

// Future Section - Timeline Compacta Alternada
export const FutureSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center animate-on-scroll mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-ecam-forest mb-8">
            Um legado em construção
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            O Carbono Social é mais do que um projeto — é um compromisso com as próximas gerações.
            Nos <strong className="text-ecam-blue">próximos 20 anos</strong>, ele continuará ampliando territórios, fortalecendo práticas regenerativas e gerando impacto real.
          </p>
        </div>

        {/* Compact Timeline */}
        <div className="max-w-3xl mx-auto">
          <div className="space-y-8">
            {mockData.timeline.map((item, index) => (
              <div 
                key={index} 
                className={`flex items-start gap-6 animate-on-scroll ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex-shrink-0">
                  <div className="bg-ecam-blue w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                    {item.icon}
                  </div>
                </div>
                
                <div className={`flex-1 bg-gray-50 p-6 rounded-xl ${index % 2 === 0 ? 'text-left' : 'text-right'}`}>
                  <h3 className="text-2xl font-bold text-ecam-red mb-2">{item.year}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section - Com logos reais
export const PartnersSection = () => {
  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl md:text-5xl font-bold text-ecam-forest mb-6">
            Parceiros
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Juntos, construímos o elo entre quem produz, quem preserva e quem investe no futuro do planeta.
          </p>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-8 max-w-5xl mx-auto">
          {mockData.partners.map((partner, index) => {
            const content = partner.isImage ? (
              <img 
                src={partner.logo} 
                alt={partner.name}
                className="max-h-20 max-w-[180px] w-auto h-auto object-contain"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="text-xl font-bold text-ecam-blue group-hover:text-ecam-celeste transition-colors">
                  {partner.logo}
                </div>
                {partner.website && partner.website !== '#' && (
                  <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-ecam-blue transition-colors" />
                )}
              </div>
            );

            const baseClasses = "bg-white p-8 rounded-xl transition-all animate-on-scroll group border-2 border-transparent flex items-center justify-center";
            const style = { animationDelay: `${index * 100}ms`, minHeight: '120px', minWidth: '200px' };

            // Se não tiver website, renderiza div; se tiver, renderiza link
            return partner.website && partner.website !== '#' ? (
              <a
                key={index}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseClasses} hover:shadow-lg hover:-translate-y-1 cursor-pointer hover:border-ecam-blue`}
                style={style}
              >
                {content}
              </a>
            ) : (
              <div
                key={index}
                className={`${baseClasses} shadow-md`}
                style={style}
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};